<template>
  <v-card flat outlined color="#eaeaea">
    <v-data-table
      class="table"
      :headers="filteredHeaders"
      :items="claims"
      hide-default-footer
      mobile-breakpoint="960"
      :items-per-page="claims.length"
    >
      <template v-slot:[`header.Explanation`]="{ header, index }">
        <span style="padding-right: 4px; text-align: right">
          {{ header.text }}
        </span>
      </template>
      <template v-slot:[`item.Id`]="{ item }">
        <td class="text-subtitle-2" :key="item.Id">
          {{ item.Index }}
        </td>
      </template>
      <template v-slot:[`item.COBAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            -
          </span>
          <span v-else>
            {{ $helpers.toCurrency(item.CurrentClaimData.COBAmount) }}
          </span>
          
        </td>
      </template>
      <template v-slot:[`item.ExpenseAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            {{ item.CurrentClaimData.DayDuration === "HalfDay"? "1/2": "1" }}
          </span>
          <span v-else>
            {{ $helpers.toCurrency(computeAmount(item)) }}
          </span>
          
        </td>
      </template>
      <template v-slot:[`item.Patient`]="{ item }">
        <td class="text-subtitle-2 text-left">
          {{ getPatientName(item) }}
        </td>
      </template>
      <template v-slot:[`item.PlanBenefitId`]="{ item }">
        <td class="text-subtitle-2 text-left" style="max-width:280px">
          {{ getBenefitName(item) }}
        </td>
      </template>
      <template v-slot:[`item.ToothCodeId`]="{ item }">
        <td class="text-subtitle-2 text-left">
          {{ getToothCode(item) }}
        </td>
      </template>
      <template v-slot:[`item.DateOfService`]="{ item }">
        <td class="text-subtitle-2 text-left" style="min-width:90px">
          <span
            v-if="!claim.IsPredetermined"
            >{{ item.CurrentClaimData.DateOfService.split("T")[0] }}</span
          >
        </td>
      </template>
      <template v-slot:[`item.ExpenseProof`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <v-icon
            :color="visited[item.Id]?'accent':'primary'"
            class="proof-eye"
            @click="previewProof(item.ExpenseProof, item.Id)"
          >
            {{ item.ExpenseProof ? "mdi-eye-outline" : "" }}
          </v-icon>
        </td>
      </template>
      <template v-slot:[`item.COBProof`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <v-icon
            :color="visited[item.Id]?'accent':'primary'"
            class="proof-eye"
            @click="previewProof(item.COBProof, item.Id)"
          >
            {{ item.COBProof ? "mdi-eye-outline" : "" }}
          </v-icon>
        </td>
      </template>
      <template v-slot:[`item.ReferralProof`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <v-icon
            :color="visited[item.Id]?'accent':'primary'"
            class="proof-eye"
            @click="previewProof(item.ReferralProof, item.Id)"
          >
            {{ item.ReferralProof ? "mdi-eye-outline" : "" }}
          </v-icon>
        </td>
      </template>
      <template v-slot:[`item.DoctorNoteProof`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <v-icon
            color="accent"
            class="proof-eye"
            @click="previewProof(item.DoctorNoteProof)"
          >
            {{ item.DoctorNoteProof ? "mdi-eye-outline" : "" }}
          </v-icon>
        </td>
      </template>
      <template v-slot:[`item.SickDayFormProof`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <v-icon
            color="accent"
            class="proof-eye"
            @click="previewProof(item.SickDayFormProof)"
          >
            {{ item.SickDayFormProof ? "mdi-eye-outline" : "" }}
          </v-icon>
        </td>
      </template>
      <template v-slot:[`item.Status`]="{ item }">
        <td class="text-subtitle-2 text-left">
          {{ item.State }}
        </td>
      </template>
      <template v-slot:[`item.EligibleAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            -
          </span>
          <span v-else>
            {{ $helpers.toCurrency(computeEligibleAmount(item)) }}
          </span>
        </td>
      </template>
      <template v-slot:[`item.DeductibleAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            -
          </span>
          <span v-else>
            {{ $helpers.toCurrency(item.CurrentClaimData.DeductibleAmount) }}
          </span>
          
        </td>
      </template>
      <template v-slot:[`item.DeductibleAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          {{ $helpers.toCurrency(item.CurrentClaimData.DeductibleAmount) }}
        </td>
      </template>
      <template v-slot:[`item.CoinsurancePercentage`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            -
          </span>
          <span v-else>
          {{ `${item.CurrentClaimData.PlanBenefit.PercentagePayable}%` }}
          </span>
        </td>
      </template>
      <template v-slot:[`item.PaidAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          {{ $helpers.toCurrency(computePaidAmount(item)) }}
        </td>
      </template>
      <template v-slot:[`item.UnpaidAmount`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay">
            -
          </span>
          <span v-else>{{ $helpers.toCurrency(computeUnpaidAmount(item)) }}</span>
        </td>
      </template>
      <template v-slot:[`item.Explanation`]="{ item }">
        <td class="text-subtitle-2 text-left">
          <span v-if="item.CurrentClaimData.AdjudicationError">
            {{ adjudicationMessage(item.CurrentClaimData.AdjudicationError) }}
          </span>
          <template
            v-if="
              item.CurrentClaimData.EOB && item.CurrentClaimData.EOB.length > 0
            "
          >
            <span
              v-for="(explanation, index) in item.CurrentClaimData.EOB"
              :key="index"
            >
              {{ adjudicationMessage(explanation) }}
            </span>
          </template>
        </td>
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:body.append="{ headers }">
        <tr v-if="isSickDay">
          <td colspan="5" class="text-right pr-0 total">
            {{ $t("components.claims_history.total") }}:
          </td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalExpense) }}
            </strong>
          </td>
          <td
            class="total co-insurance"
            v-if="headers.filter(o=>o.value ==='CoinsurancePercentage').length > 0"
          ></td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalPaid) }}
            </strong>
          </td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalUnpaid) }}
            </strong>
          </td>
          <td colspan="3" class="total">
            <!-- {{ $t("components.claims_history.header.claimed_total") }}:
            <strong>{{ $helpers.toCurrency(claimedTotal) }}</strong> -->
          </td>
        </tr>
        <tr v-else>
          <td colspan="5" class="text-right pr-0 total" v-if="isDental">
            {{ $t("components.claims_history.total") }}:
          </td>
          <td colspan="4" class="text-right pr-0 total" v-else>
            {{ $t("components.claims_history.total") }}:
          </td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalExpense) }}
            </strong>
          </td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalCOB) }}
            </strong>
          </td>
          <td colspan="1" class="total" v-if="hideProofs"></td>
          <td :colspan="computeProofDistance()" class="total" v-if="!hideProofs"></td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalEligible) }}
            </strong>
          </td>
          <td
            class="total co-insurance"
            v-if="headers.filter(o=>o.value ==='CoinsurancePercentage').length > 0"
          ></td>
          <td
            colspan="1"
            class="total hide-proofs-false"
            v-if="!hideProofs"
          ></td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalPaid) }}
            </strong>
          </td>
          <td class="total">
            <strong class="text--lighten-3">
              {{ $helpers.toCurrency(totalUnpaid) }}
            </strong>
          </td>
          <td colspan="3" class="total">
            <!-- {{ $t("components.claims_history.header.claimed_total") }}:
            <strong>{{ $helpers.toCurrency(claimedTotal) }}</strong> -->
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { translate2 } from "@/trans2";
export default {
  props: {
    claim: Object,
    hideProofs: Boolean,
  },
  data() {
    return {
      claims: [],
      visited: {}
    };
  },
  computed: {
    filteredHeaders() {
      let headers = this.headers.filter(
        (h) =>
          (h.value !== "ExpenseAmount" || this.hasExpense) &&
          (h.value !== "COBAmount" || this.hasCOB) &&
          (h.value !== "ExpenseProof" || this.hasExpenseProof) &&
          (h.value !== "COBProof" || this.hasCOBProof) &&
          (h.value !== "ReferralProof" || this.hasReferralProof) &&
          (h.value !== "SickDayFormProof" || this.hasSickDayFormProof) &&
          (h.value !== "DoctorNoteProof" || this.hasDoctorNoteProof) &&
          (h.value !== "EligibleAmount" || this.hasEligibleAmount)
      );
      if (this.hideProofs) {
        return headers.filter((h) => {
          h.sortable = false;
          return !h.value.includes("Proof");
        });
      } else {
        return headers;
      }
    },
    hasExpense() {
      return this.claim.Claims.some(
        (c) =>
          c.CurrentClaimData.ExpenseAmount > 0 ||
          c?.CurrentClaimData?.PlanBenefit?.Benefit?.Name != "Sick Day"
      );
    },
    hasCOB() {
      return this.claim.Claims.some(
        (c) =>
          c.COBAmount > 0 ||
          c?.CurrentClaimData?.PlanBenefit?.Benefit?.Name != "Sick Day"
      );
    },
    hasEligibleAmount() {
      return this.claim.Claims.some(
        (c) =>
          c.CurrentClaimData.EligibleAmount > 0 ||
          c?.CurrentClaimData?.PlanBenefit?.Benefit?.Name != "Sick Day"
      );
    },
    hasExpenseProof() {
      return this.claim.Claims.some((c) => c.ExpenseProof);
    },
    hasCOBProof() {
      return this.claim.Claims.some((c) => c.COBProof);
    },
    hasReferralProof() {
      return this.claim.Claims.some((c) => c.ReferralProof);
    },
    hasDoctorNoteProof() {
      return this.claim.Claims.some((c) => c.DoctorNoteProof);
    },
    hasSickDayFormProof() {
      return this.claim.Claims.some((c) => c.SickDayFormProof);
    },
    headers() {
      const health = [
        {
          text: "#",
          align: "center",
          sortable: false,
          value: "Id",
          cellClass: "text-subtitle-2 px-1",
          class: `header-text no-header`,
          width: "20px",
        },
        {
          text: this.$t("components.claims_history.header.patient"),
          align: "left",
          sortable: false,
          value: "Patient",
          cellClass: "px-1",
          class: `header-text2`,
        },
        {
          text: this.$t("globals.benefit"),
          align: "left",
          sortable: false,
          value: "PlanBenefitId",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.expenses_table.date_of_expense"),
          align: "left",
          sortable: false,
          value: "DateOfService",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.claims_history.header.total_expense"),
          align: "left",
          sortable: false,
          value: "ExpenseAmount",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.claims_history.header.total_cob"),
          align: "left",
          sortable: false,
          value: "COBAmount",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text header`,
        },
        {
          text: this.$t("components.claims_history.header.receipt"),
          align: "left",
          sortable: false,
          value: "ExpenseProof",
          cellClass: "text-subtitle-2 px-2",
          class: `secondary--text header-text header`,
          width: "60px",
        },
        {
          text: this.$t("components.claims_history.header.cob_proof"),
          align: "left",
          sortable: false,
          value: "COBProof",
          cellClass: "text-subtitle-2 px-2",
          class: `secondary--text header-text`,
          width: "60px",
        },
        {
          text: this.$t("components.expenses_table.referral"),
          align: "left",
          sortable: false,
          value: "ReferralProof",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
          width: "60px",
        },
        {
          text: this.$t("enums.DoctorNote"),
          align: "left",
          sortable: false,
          value: "DoctorNoteProof",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
          width: "60px",
        },
        {
          text: this.$t("enums.SickDayForm"),
          align: "left",
          sortable: false,
          value: "SickDayFormProof",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
          width: "60px",
        },
        {
          text: this.$t("globals.status"),
          align: "left",
          sortable: true,
          value: "Status",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.eligible_amount"),
          align: "left",
          sortable: true,
          value: "EligibleAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.deductible"),
          align: "left",
          sortable: true,
          value: "DeductibleAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.coinsurance_percentage"),
          align: "left",
          sortable: true,
          value: "CoinsurancePercentage",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.paid_amount"),
          align: "left",
          sortable: true,
          value: "PaidAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.unpaid_amount"),
          align: "left",
          sortable: true,
          value: "UnpaidAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.explanation"),
          align: "left",
          sortable: true,
          value: "Explanation",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
      ];
      const dental = [
        {
          text: "#",
          align: "center",
          sortable: false,
          value: "Id",
          cellClass: "text-subtitle-2 px-1",
          class: `header-text no-header`,
          width: "20px",
        },
        {
          text: this.$t("components.claims_history.header.patient"),
          align: "left",
          sortable: false,
          value: "Patient",
          cellClass: "px-1",
          class: `header-text2`,
        },
        {
          text: this.$t("globals.benefit"),
          align: "left",
          sortable: false,
          value: "PlanBenefitId",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.expenses_table.tooth_code"),
          align: "left",
          sortable: false,
          value: "ToothCodeId",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.expenses_table.date_of_expense"),
          align: "left",
          sortable: false,
          value: "DateOfService",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.claims_history.header.total_expense"),
          align: "left",
          sortable: false,
          value: "ExpenseAmount",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text`,
        },
        {
          text: this.$t("components.claims_history.header.total_cob"),
          align: "left",
          sortable: false,
          value: "COBAmount",
          cellClass: "text-subtitle-2 px-2",
          class: `header-text header`,
        },
        {
          text: this.$t("components.claims_history.header.receipt"),
          align: "left",
          sortable: false,
          value: "ExpenseProof",
          cellClass: "text-subtitle-2 px-2",
          class: `secondary--text header-text header`,
          width: "60px",
        },
        {
          text: this.$t("components.claims_history.header.cob_proof"),
          align: "left",
          sortable: false,
          value: "COBProof",
          cellClass: "text-subtitle-2 px-2",
          class: `secondary--text header-text`,
          width: "60px",
        },
        {
          text: this.$t("components.expenses_table.referral"),
          align: "left",
          sortable: false,
          value: "ReferralProof",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
          width: "60px",
        },
        {
          text: this.$t("globals.status"),
          align: "left",
          sortable: true,
          value: "Status",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.eligible_amount"),
          align: "left",
          sortable: true,
          value: "EligibleAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.deductible"),
          align: "left",
          sortable: true,
          value: "DeductibleAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.coinsurance_percentage"),
          align: "left",
          sortable: true,
          value: "CoinsurancePercentage",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.paid_amount"),
          align: "left",
          sortable: true,
          value: "PaidAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.expenses_table.unpaid_amount"),
          align: "left",
          sortable: true,
          value: "UnpaidAmount",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
        {
          text: this.$t("components.claims_history.header.explanation"),
          align: "left",
          sortable: true,
          value: "Explanation",
          cellClass: "text-subtitle-2 px-2",
          class: "secondary--text header-text",
        },
      ];
      if (this.isDental) {
        return dental;
      }
      return health;
    },
    isDental() {
      return (
        this.claim.Claims[0].CurrentClaimData.ToothCode !== null &&
        this.claim.Claims[0].CurrentClaimData.ToothCode !== undefined
      );
    },
    isSickDay() {
      console.log(JSON.stringify(this.claim));
      if (this.claim === null || this.claim === undefined) {
        return false;
      }
      if (this.claim.Claims === null || this.claim.Claims === undefined) {
        return false;
      }
      if (this.claim.Claims[0] === null || this.claim.Claims[0] === undefined) {
        return false;
      }
      if (this.claim.Claims[0].CurrentClaimData === null || this.claim.Claims[0].CurrentClaimData === undefined) {
        return false;
      }
      if (this.claim.Claims[0].CurrentClaimData.PlanBenefit === null || this.claim.Claims[0].CurrentClaimData.PlanBenefit === undefined) {
        return false
      }
     return this.claim.Claims[0].CurrentClaimData.PlanBenefit.Benefit.IsSickDay
    },
    totalExpense() {
      let total = 0;
      // this.claim.Claims.forEach((c) => (total += this.computeAmount(c)));
      for (const c of this.claim.Claims) {
        if (c.State === "Deleted") {
          break;
        }
        total += this.computeAmount(c);
      }
      return total;
    },
    totalCOB() {
      let total = 0;
      this.claim.Claims.forEach((c) => (total += c.CurrentClaimData.COBAmount));
      return total;
    },
    totalPaid() {
      let total = 0;
      this.claim.Claims.filter(
        (o) =>
          o.State === "Approved" ||
          o.State === "PendingPayment" ||
          o.State === "Paid" ||
          o.State === "Archived"
      ).forEach((c) => (total += this.computePaidAmount(c)));
      return total;
    },
    totalUnpaid() {
      let total = 0;
      this.claim.Claims.filter(
        (o) =>
          o.State === "Approved" ||
          o.State === "PendingPayment" ||
          o.State === "Paid" ||
          o.State === "Archived"
      ).forEach((c) => (total += this.computeUnpaidAmount(c)));
      return total;
    },
    totalEligible() {
      let total = 0;
      this.claim.Claims.filter(
        (o) =>
          o.State === "Approved" ||
          o.State === "PendingPayment" ||
          o.State === "Paid" ||
          o.State === "Archived"
      ).forEach((c) => (total += this.computeEligibleAmount(c)));
      return total;
    },
    claimedTotal() {
      return this.totalExpense - this.totalCOB;
    },
  },
  watch: {
    claim: {
      handler(v) {
        this.getProofs();
        this.addIndexToClaims(v);
      },
    },
  },
  created() {
    // console.log("created: ", this.claim);
    this.getProofs();
    this.addIndexToClaims(this.claim);
  },
  methods: {
    computeProofDistance() {
      let total = 1;
      if (this.hasExpenseProof) {
        total++;
      }
      if (this.hasCOBProof) {
        total++;
      }
      if (this.hasReferralProof) {
        total++;
      }
      if (this.hasDoctorNoteProof) {
        total++;
      }
      if (this.hasSickDayFormProof) {
        total++;
      }
      return total;
    },
    addIndexToClaims(claim) {
      console.log("----------------------------------------------------------claim detail is: ",claim)
      this.claims = [];
      if (claim && claim.Claims) {
        this.claims = claim.Claims;
        let i = 1;
        for (let claim of this.claims) {
          claim.Index = i;
          i++;
        }
      }
    },
    getBenefitName(claim) {
      return `${claim.CurrentClaimData.PlanBenefit.Benefit.Code} - ${translate2(
        claim.CurrentClaimData.PlanBenefit.Benefit,
        "Name"
      )}`;
    },
    getToothCode(claim) {
      if (
        claim.CurrentClaimData.ToothCode === null ||
        claim.CurrentClaimData.ToothCode === undefined
      ) {
        return "";
      }
      return claim.CurrentClaimData.ToothCode.Code;
    },
    getPatientName(claim) {
      return claim.FirstName + " " + claim.LastName;
    },
    getProofs() {
      // this.claim.CurrentClaimData.Proofs.forEach((p) => {
      //   if (p.ClaimLines && p.ClaimLines.length) {
      //     p.ClaimLines.forEach((l) => {
      //       // eslint-disable-next-line vue/no-mutating-props
      //       this.claim.Claims[l][p.ProofType + "Proof"] = p;
      //     });
      //   }
      //   this.claim.Claims[l][p.ProofType + "Proof"] = p;
      // });
      for (let claim of this.claim.Claims) {
        if (claim.Proofs && Array.isArray(claim.Proofs)) {
          for (let proof of claim.Proofs) {
            claim[proof.ProofType + "Proof"] = proof;
          }
        }
      }
    },
    previewProof(proof, claimId) {
      this.visited[claimId] = true;
      this.$emit("preview-proof", proof);
      console.log(this.visited);
      this.$forceUpdate();
    },
    adjudicationMessage(item) {
      if (item.code === "900" || item.code === "901") {
        if (item.message!==null) {
          return item.message;
        }
        return item.genericMessage;
      }
      return this.$t(
        `adjudication_message.${item.message}`,
        item.formattedParams
      );
    },
    computePaidAmount(item) {
      const paidLabFee = parseFloat(item.CurrentClaimData.PaidLabFee);
      if (isNaN(paidLabFee)) {
        return item.CurrentClaimData.PaidAmount;
      }
      return item.CurrentClaimData.PaidAmount + paidLabFee;
    },
    computeUnpaidAmount(item) {
      if (item.CurrentClaimData.PlanBenefit && item.CurrentClaimData.PlanBenefit.Benefit.IsSickDay) {
        return 0.0;
      }
      let cob = parseFloat(item.CurrentClaimData.COBAmount);
      if (isNaN(cob)) {
        cob = 0.0;
      }
      let paid = parseFloat(item.CurrentClaimData.PaidAmount);
      if (isNaN(paid)) {
        paid = 0.0;
      }
      let paidLabFee = parseFloat(item.CurrentClaimData.PaidLabFee);
      if (isNaN(paidLabFee)) {
        paidLabFee = 0.0;
      }
      let labFee = parseFloat(item.CurrentClaimData.LabFee);
      if (isNaN(labFee)) {
        labFee = 0.0;
      }
      return item.CurrentClaimData.ExpenseAmount + labFee - cob - paid - paidLabFee;
    },
    computeEligibleAmount(item) {
      const labFee = parseFloat(item.CurrentClaimData.EligibleLabFee);
      if (isNaN(labFee)) {
        return item.CurrentClaimData.EligibleAmount;
      }
      return item.CurrentClaimData.EligibleAmount + labFee;
    },
    computeAmount(item) {
      const labFee = parseFloat(item.CurrentClaimData.LabFee);
      if (isNaN(labFee)) {
        return item.CurrentClaimData.ExpenseAmount;
      }
      return item.CurrentClaimData.ExpenseAmount + labFee;
    },
  },
};
</script>
<style scoped>
.table {
  /* background-color: #eaeaea; */
}
.table >>> tr:hover {
  background-color: transparent !important;
}
.table >>> td {
  font-size: 0.75rem !important;
  font-weight: 500;
  border-bottom: none !important;
}
.table >>> th {
  height: 20px !important;
  color: grey !important;
}
.table >>> td {
  height: 20px !important;
}
.total {
  border-top: 1px solid grey !important;
  height: 20px !important;
}
.proof-eye:hover {
  color: var(--primary) !important;
}
</style>
